import React, { useEffect, useRef, useState } from 'react'
import CodeMirror, { ReactCodeMirrorRef, Extension } from '@uiw/react-codemirror'
import { json as cmJson } from '@codemirror/lang-json'
import { EditorView } from '@codemirror/view'
import { previewOf, prettyOf, validateJsonObject } from 'components/spreadsheet/helpers/jsonCell'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import { useResolvedTheme } from 'hooks/useTheme'
import { IJsonCellValue } from 'types'

interface JsonCellProps {
  value: IJsonCellValue | null
  align: 'center' | 'left' | 'right'
  color: string
  readOnly: boolean
  rowHeight: number
  selected: boolean
  editing: boolean
  isExpanded: boolean
  columnId: string | undefined
  rowId: string | undefined
  setCellValue: (value: IJsonCellValue | null, onSuccess?: () => void, onError?: (message?: string) => void) => void
  // Surfaces a client-side validation failure (invalid JSON / not an object) to
  // the user. Wired by the parent Cell to the global error snackbar so the
  // message is visible rather than buried in the inline editor.
  onValidationError?: (message: string) => void
}

const styles = cellStyles()

// The app's own tokens, so the editor matches the text cell editor in both themes;
// CodeMirror's built-in light theme left it white, with the cell's light dark-mode
// text on top, in dark mode.
const resizableEditor = (isExpanded: boolean): Extension =>
  EditorView.theme({
    '&.cm-editor': {
      backgroundColor: 'var(--color-surface)',
      color: 'var(--color-body)'
    },
    '.cm-cursor, .cm-dropCursor': {
      borderLeftColor: 'var(--color-body)'
    },
    '&': {
      resize: 'both',
      overflow: 'hidden',
      width: 'max(var(--cell-width, 200px), 270px)',
      minWidth: 'var(--cell-width, 200px)',
      minHeight: '45px',
      maxHeight: isExpanded ? '60vh' : '240px'
    },
    '.cm-scroller': { overflow: 'auto' }
  })

const JsonCell: React.FC<JsonCellProps> = ({
  value,
  align,
  color,
  readOnly,
  rowHeight,
  selected,
  editing,
  isExpanded,
  setCellValue,
  onValidationError
}) => {
  const resolvedTheme = useResolvedTheme()
  const [draft, setDraft] = useState<string>(prettyOf(value))
  const [error, setError] = useState<string | null>(null)
  // Negative margins that pull the inline editor back inside the grid's visible box when it
  // would otherwise overhang the bottom or the right-hand edge. `up`/`left` are both >= 0 and
  // applied as negative margin-top / margin-left.
  const [overhangShift, setOverhangShift] = useState<{ up: number; left: number }>({ up: 0, left: 0 })
  const draftRef = useRef(draft)
  const editorRef = useRef<ReactCodeMirrorRef>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Mirrors `overhangShift` so the measuring effect can subtract the shift it already applied and
  // always reason about the editor's un-shifted position. Without this, a re-measure (e.g. when
  // `isExpanded` flips) would see the corrected box, find no overhang, and reset to 0.
  const overhangShiftRef = useRef(overhangShift)

  useEffect(() => {
    const next = prettyOf(value)
    setDraft(next)
    draftRef.current = next
    setError(null)
  }, [value])

  // Focus the editor when the cell opens for editing (double-click / form view).
  // Done via a ref rather than the `autoFocus` prop (forbidden by jsx-a11y).
  useEffect(() => {
    if (!editing || readOnly) return
    const frameId = requestAnimationFrame(() => editorRef.current?.view?.focus())
    return () => cancelAnimationFrame(frameId)
  }, [editing, readOnly])

  // Keep the inline editor inside the grid's visible box: shift it up if it would overhang the
  // bottom (behind the sticky aggregates bar), and left if it would overhang the right-hand edge.
  useEffect(() => {
    const applyShift = (next: { up: number; left: number }) => {
      overhangShiftRef.current = next
      setOverhangShift(next)
    }

    if (!editing || readOnly) {
      applyShift({ up: 0, left: 0 })
      return
    }
    const frameId = requestAnimationFrame(() => {
      const wrapper = wrapperRef.current
      const scrollContainer = wrapper?.closest('[data-testid="data-view-spreadsheet"]') as HTMLElement | null
      if (!wrapper || !scrollContainer) return

      const applied = overhangShiftRef.current
      const wrapperRect = wrapper.getBoundingClientRect()
      const containerRect = scrollContainer.getBoundingClientRect()

      const footer = scrollContainer.querySelector('#footer') as HTMLElement | null
      const bottomEdge = footer ? footer.getBoundingClientRect().top : containerRect.bottom
      const up = Math.max(wrapperRect.bottom + applied.up - bottomEdge, 0)

      // clientWidth excludes the vertical scrollbar gutter, which getBoundingClientRect() includes.
      const rightEdge = containerRect.left + scrollContainer.clientWidth
      const overhangRight = wrapperRect.right + applied.left - rightEdge
      // Never drag the editor's left edge past the left edge of the visible grid — an editor wider
      // than the viewport should start at the left and clip on the right rather than lose its start.
      const maxLeftShift = Math.max(wrapperRect.left + applied.left - containerRect.left, 0)
      const left = overhangRight > 0 ? Math.min(overhangRight, maxLeftShift) : 0

      applyShift({ up, left })
    })
    return () => cancelAnimationFrame(frameId)
  }, [editing, readOnly, isExpanded])

  // Mirror the text cell: render a single-line preview until the cell is opened
  // for editing (double-click in the grid, or focusing it in form view), then
  // expand inline into a JSON editor. We deliberately do not use a modal — the
  // inline editor is the "expanded text" view.
  if (!editing || readOnly) {
    return (
      <div
        data-testid="json-cell-preview"
        className={styles.value({ focused: selected, class: 'cell-value' })}
        data-focussed={selected || undefined}
        tabIndex={-1}
        style={{
          textAlign: align,
          color,
          width: 'var(--cell-width)',
          maxWidth: 'var(--cell-width)',
          maxHeight: `${getRowHeightVariable(rowHeight) - 1}px`
        }}
      >
        <div className={styles.content({ class: 'overflow-hidden text-ellipsis whitespace-nowrap font-mono' })}>
          {previewOf(value)}
        </div>
      </div>
    )
  }

  const commit = (text: string) => {
    const result = validateJsonObject(text)
    if (!result.ok) {
      setError(result.error)
      onValidationError?.(result.error)
      return
    }
    setError(null)
    setCellValue(result.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      commit(draftRef.current)
    }
  }

  const handleBlur = (e: React.FocusEvent) => {
    // Only commit if focus is leaving the entire wrapper (not just moving within it).
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      commit(draftRef.current)
    }
  }

  return (
    <div
      ref={wrapperRef}
      data-testid="json-cell-editor"
      className="relative bg-surface w-fit transition-[margin-top,margin-left] duration-200 ease-in-out"
      style={{
        marginTop: overhangShift.up > 0 ? `-${overhangShift.up}px` : undefined,
        marginLeft: overhangShift.left > 0 ? `-${overhangShift.left}px` : undefined
      }}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      <CodeMirror
        ref={editorRef}
        value={draft}
        editable
        theme={resolvedTheme}
        extensions={[cmJson(), EditorView.lineWrapping, resizableEditor(isExpanded)]}
        onChange={(v) => {
          setDraft(v)
          draftRef.current = v
          if (error) setError(null)
        }}
      />
      {error && (
        <div data-testid="json-cell-error" className="text-red text-xs px-1 py-0.5">
          {error}
        </div>
      )}
    </div>
  )
}

export default JsonCell
